import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  MenuItem,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
} from "@mui/material";
import { EventFilterRequest, EVENT_TYPES, CompanyDropdownDto } from "../services/Dtos/EventsDtos";
import { Search as SearchIcon, Clear as ClearIcon, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { HelpersService } from "../services/HerlpersService";
import showToast from "../helpers/notification";
import { getEventTypeHexColor } from "../helpers/eventTypeColors";

// US States list with code, name, and default center coordinates (lat, lng)
const US_STATES = [
  { code: "AL", name: "Alabama", lat: 32.8067, lng: -86.7113 },
  { code: "AK", name: "Alaska", lat: 64.2008, lng: -152.2782 },
  { code: "AZ", name: "Arizona", lat: 33.7298, lng: -111.4312 },
  { code: "AR", name: "Arkansas", lat: 34.9697, lng: -92.3731 },
  { code: "CA", name: "California", lat: 36.1162, lng: -119.6816 },
  { code: "CO", name: "Colorado", lat: 39.0598, lng: -105.3111 },
  { code: "CT", name: "Connecticut", lat: 41.5978, lng: -72.7554 },
  { code: "DE", name: "Delaware", lat: 39.3185, lng: -75.4769 },
  { code: "FL", name: "Florida", lat: 27.6648, lng: -81.5158 },
  { code: "GA", name: "Georgia", lat: 33.0406, lng: -83.6431 },
  { code: "HI", name: "Hawaii", lat: 21.0943, lng: -157.4983 },
  { code: "ID", name: "Idaho", lat: 44.2405, lng: -114.4787 },
  { code: "IL", name: "Illinois", lat: 40.3495, lng: -88.9861 },
  { code: "IN", name: "Indiana", lat: 39.8494, lng: -86.2584 },
  { code: "IA", name: "Iowa", lat: 42.0115, lng: -93.2105 },
  { code: "KS", name: "Kansas", lat: 38.5266, lng: -96.7265 },
  { code: "KY", name: "Kentucky", lat: 37.6681, lng: -84.6701 },
  { code: "LA", name: "Louisiana", lat: 31.1695, lng: -91.8749 },
  { code: "ME", name: "Maine", lat: 44.6939, lng: -69.3819 },
  { code: "MD", name: "Maryland", lat: 39.0639, lng: -76.8021 },
  { code: "MA", name: "Massachusetts", lat: 42.2302, lng: -71.5301 },
  { code: "MI", name: "Michigan", lat: 43.3266, lng: -84.5361 },
  { code: "MN", name: "Minnesota", lat: 45.6945, lng: -93.9196 },
  { code: "MS", name: "Mississippi", lat: 32.3792, lng: -89.2453 },
  { code: "MO", name: "Missouri", lat: 38.4561, lng: -92.2884 },
  { code: "MT", name: "Montana", lat: 46.9219, lng: -103.6006 },
  { code: "NE", name: "Nebraska", lat: 41.4925, lng: -99.9018 },
  { code: "NV", name: "Nevada", lat: 38.8026, lng: -116.4194 },
  { code: "NH", name: "New Hampshire", lat: 43.4525, lng: -71.3128 },
  { code: "NJ", name: "New Jersey", lat: 40.0583, lng: -74.4057 },
  { code: "NM", name: "New Mexico", lat: 34.8405, lng: -106.2371 },
  { code: "NY", name: "New York", lat: 42.1657, lng: -74.9481 },
  { code: "NC", name: "North Carolina", lat: 35.6301, lng: -79.8064 },
  { code: "ND", name: "North Dakota", lat: 47.5289, lng: -99.784 },
  { code: "OH", name: "Ohio", lat: 40.3888, lng: -82.764 },
  { code: "OK", name: "Oklahoma", lat: 35.5653, lng: -96.9289 },
  { code: "OR", name: "Oregon", lat: 43.8041, lng: -120.5542 },
  { code: "PA", name: "Pennsylvania", lat: 40.5908, lng: -77.2098 },
  { code: "RI", name: "Rhode Island", lat: 41.6809, lng: -71.5118 },
  { code: "SC", name: "South Carolina", lat: 33.8361, lng: -80.9066 },
  { code: "SD", name: "South Dakota", lat: 44.2998, lng: -99.4388 },
  { code: "TN", name: "Tennessee", lat: 35.7478, lng: -86.6923 },
  { code: "TX", name: "Texas", lat: 31.9686, lng: -99.9018 },
  { code: "UT", name: "Utah", lat: 39.3210, lng: -111.0937 },
  { code: "VT", name: "Vermont", lat: 43.9695, lng: -72.5754 },
  { code: "VA", name: "Virginia", lat: 37.7693, lng: -78.1694 },
  { code: "WA", name: "Washington", lat: 47.7511, lng: -120.7401 },
  { code: "WV", name: "West Virginia", lat: 38.4912, lng: -82.9006 },
  { code: "WI", name: "Wisconsin", lat: 44.2685, lng: -89.6165 },
  { code: "WY", name: "Wyoming", lat: 42.755, lng: -107.3025 },
];

interface EventsFilterProps {
  onFilter: (filters: EventFilterRequest) => void;
  onStateSelected?: (stateCode: string, lat: number, lng: number) => void;
  onResetZoom?: () => void;
  onShowHeatmap?: () => void;
  loading?: boolean;
}

const EventsFilter: React.FC<EventsFilterProps> = ({ onFilter, onStateSelected, onResetZoom, onShowHeatmap, loading }) => {
  const [filters, setFilters] = useState<EventFilterRequest>({
    apiKey: "",
    dateFrom: "",
    dateTo: "",
    eventType: "",
    state: "",
    city: "",
    metroCode: "",
    zipCode: "",
    trackingNumbers: "",
    failureReason: "",
  });

  const [companies, setCompanies] = useState<CompanyDropdownDto[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(true);

  // Fetch companies on component mount
  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const data = await HelpersService.getCompaniesDropdown();
        setCompanies(data);
      } catch (err) {
        console.error("Error fetching companies:", err);
        showToast("error", "Failed to load companies. Please refresh the page.");
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Clear failureReason if eventType is changed away from DELIVERY_FAILURE
    if (name === "eventType" && value !== "DELIVERY_FAILURE") {
      setFilters((prev) => ({
        ...prev,
        [name]: value,
        failureReason: "",
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Convert local datetime to UTC ISO format
    const convertToUTC = (localDateTimeString: string): string => {
      if (!localDateTimeString) return "";

      // datetime-local format is "YYYY-MM-DDTHH:mm"
      // Create a Date object from the local time string
      const date = new Date(localDateTimeString);

      // Convert to ISO string in UTC
      return date.toISOString();
    };

    // Remove empty fields before submitting and convert dates to UTC
    const cleanedFilters: EventFilterRequest = Object.entries(filters).reduce(
      (acc, [key, value]) => {
        if (value && value.trim() !== "") {
          // Convert date fields to UTC format
          if (key === "dateFrom" || key === "dateTo") {
            acc[key as keyof EventFilterRequest] = convertToUTC(value);
          } else {
            acc[key as keyof EventFilterRequest] = value;
          }
        }
        return acc;
      },
      {} as EventFilterRequest
    );

    onFilter(cleanedFilters);

    // If a state was selected, trigger zoom to that state
    if (cleanedFilters.state && onStateSelected) {
      const selectedState = US_STATES.find((state) => state.code === cleanedFilters.state);
      if (selectedState) {
        onStateSelected(selectedState.code, selectedState.lat, selectedState.lng);
      }
    }
  };

  const handleClear = () => {
    const clearedFilters: EventFilterRequest = {
      apiKey: "",
      dateFrom: "",
      dateTo: "",
      eventType: "",
      state: "",
      city: "",
      metroCode: "",
      zipCode: "",
      trackingNumbers: "",
      failureReason: "",
    };
    setFilters(clearedFilters);
    onFilter({});
    // Reset zoom to default level 5
    onResetZoom?.();
  };

  return (
    <Card className="events-filter-card mb-4">
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" gutterBottom>
              Filter Events
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {!filters.dateFrom && !filters.dateTo
                ? "No date range specified - defaults to last 7 days"
                : "Filter events by date range and other criteria"}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box mt={3}>
            <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Tracking Numbers - Full Width */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tracking Numbers"
                name="trackingNumbers"
                value={filters.trackingNumbers}
                onChange={handleChange}
                placeholder="Enter tracking numbers separated by commas (e.g., TRK001, TRK002)"
                size="small"
              />
            </Grid>

            {/* Company Dropdown */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Company"
                name="apiKey"
                value={filters.apiKey}
                onChange={handleChange}
                size="small"
                disabled={loadingCompanies}
                InputProps={{
                  endAdornment: loadingCompanies ? (
                    <CircularProgress size={20} style={{ marginRight: 8 }} />
                  ) : null,
                }}
              >
                <MenuItem value="">
                  <em>{loadingCompanies ? "Loading companies..." : "All Companies"}</em>
                </MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.apiKey}>
                    {company.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Event Type */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Event Type"
                name="eventType"
                value={filters.eventType}
                onChange={handleChange}
                size="small"
              >
                <MenuItem value="">
                  <em>All Event Types</em>
                </MenuItem>
                {EVENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: getEventTypeHexColor(type),
                          flexShrink: 0,
                        }}
                      />
                      <span>{type.replace(/_/g, " ")}</span>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Date From */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date From"
                name="dateFrom"
                type="datetime-local"
                value={filters.dateFrom}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </Grid>

            {/* Date To */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date To"
                name="dateTo"
                type="datetime-local"
                value={filters.dateTo}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </Grid>

            {/* State */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                select
                label="State"
                name="state"
                value={filters.state}
                onChange={handleChange}
                size="small"
              >
                <MenuItem value="">
                  <em>All States</em>
                </MenuItem>
                {US_STATES.map((state) => (
                  <MenuItem key={state.code} value={state.code}>
                    {state.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* City */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={filters.city}
                onChange={handleChange}
                placeholder="e.g., Los Angeles"
                size="small"
              />
            </Grid>

            {/* Metro Code */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Metro Code"
                name="metroCode"
                value={filters.metroCode}
                onChange={handleChange}
                placeholder="Enter metro code"
                size="small"
              />
            </Grid>

            {/* Zip Code */}
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Zip Code"
                name="zipCode"
                value={filters.zipCode}
                onChange={handleChange}
                placeholder="e.g., 90001"
                size="small"
              />
            </Grid>

            {/* Failure Reason - Only shown when DELIVERY_FAILURE is selected */}
            {filters.eventType === "DELIVERY_FAILURE" && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Failure Reason"
                  name="failureReason"
                  value={filters.failureReason}
                  onChange={handleChange}
                  placeholder="Enter failure reason"
                  size="small"
                />
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ClearIcon />}
                  onClick={handleClear}
                  disabled={loading}
                >
                  Clear Filters
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  disabled={loading}
                >
                  {loading ? "Searching..." : "Search Events"}
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  disabled={loading}
                  onClick={onShowHeatmap}
                >
                  Show Heatmap
                </Button>
              </Box>
            </Grid>
          </Grid>
            </form>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default EventsFilter;
